import { Box, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { DataAdaptorType } from "../../services/adminDataAdaptorService";
import postgresqlLogo from "../../assets/images/adaptors/postgresql.svg?raw";
import mysqlLogo from "../../assets/images/adaptors/mysql.svg?raw";
import mongodbLogo from "../../assets/images/adaptors/mongodb.svg?raw";
import trinoLogo from "../../assets/images/adaptors/trino.svg?raw";
import clickhouseLogo from "../../assets/images/adaptors/clickhouse.svg?raw";
import hiveLogo from "../../assets/images/adaptors/apachehive.svg?raw";
import sparkLogo from "../../assets/images/adaptors/apachespark.svg?raw";
import databricksLogo from "../../assets/images/adaptors/databricks.svg?raw";
import snowflakeLogo from "../../assets/images/adaptors/snowflake.svg?raw";
import bigqueryLogo from "../../assets/images/adaptors/googlebigquery.svg?raw";
import redshiftLogo from "../../assets/images/adaptors/amazonredshift.svg?raw";

const LOGO_BY_TYPE: Partial<Record<DataAdaptorType, string>> = {
  POSTGRESQL: postgresqlLogo,
  MYSQL: mysqlLogo,
  MONGODB: mongodbLogo,
  TRINO: trinoLogo,
  CLICKHOUSE: clickhouseLogo,
  HIVE: hiveLogo,
  SPARK_SQL: sparkLogo,
  DATABRICKS: databricksLogo,
  SNOWFLAKE: snowflakeLogo,
  BIGQUERY: bigqueryLogo,
  REDSHIFT: redshiftLogo,
};

const LABEL_BY_TYPE: Partial<Record<DataAdaptorType, string>> = {
  POSTGRESQL: "PostgreSQL",
  MYSQL: "MySQL",
  MONGODB: "MongoDB",
  TRINO: "Trino / Presto",
  CLICKHOUSE: "ClickHouse",
  HIVE: "Apache Hive",
  SPARK_SQL: "Spark SQL",
  DATABRICKS: "Databricks",
  SNOWFLAKE: "Snowflake",
  BIGQUERY: "BigQuery",
  REDSHIFT: "Redshift",
};

type Props = {
  type?: DataAdaptorType | string | null;
  size?: number;
  label?: string;
};

/** Inline currentColor SVG mark that follows light/dark theme icon colors. */
export default function AdaptorTypeIcon({ type, size = 32, label }: Props) {
  const theme = useTheme();
  const key = (type || "") as DataAdaptorType;
  const svg = LOGO_BY_TYPE[key];
  const title = label || LABEL_BY_TYPE[key] || key || "Adaptor";
  const isDark = theme.palette.mode === "dark";

  if (!svg) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: 1,
          bgcolor: "action.hover",
          border: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
        aria-hidden
      />
    );
  }

  const icon = (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 1,
        bgcolor: isDark ? "rgba(255,255,255,0.06)" : "action.hover",
        border: 1,
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        p: `${Math.max(5, Math.round(size * 0.18))}px`,
        boxSizing: "border-box",
        color: isDark ? "common.white" : "text.primary",
        "& svg": {
          width: "100%",
          height: "100%",
          display: "block",
        },
      }}
      aria-hidden
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );

  return (
    <Tooltip title={title} placement="top" arrow>
      <span style={{ display: "inline-flex", lineHeight: 0 }}>{icon}</span>
    </Tooltip>
  );
}
